import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type { Attribute } from './attribute'
import { Entity } from './entity'
import type { Id as _Id } from './id'
import { Snapshot } from './snapshot'
import { Type as _Type } from './type'

/**
 * Data model that has corresponding {@link Record}
 */
export type ForRecord = Entity | Snapshot
export namespace ForRecord {
  /**
   * Valid {@link Type} for a {@link ForRecord}
   */
  export type Type = _Id.Parts.Of<Id>['type']
  export namespace Type {
    const _ALL = [_Type.Entity, _Type.Snapshot] as const
    /** Ensures we aren't missing anything in {@link _ALL} */
    type VerifyAll = Satisfies<Type, (typeof _ALL)[number]>

    export const ALL: readonly Type[] = _ALL

    export const isType: TypeChecker<Type> = TypeChecker.Factory.oneOf(ALL)
  }
  /**
   * Identifies a {@link ForRecord}
   */

  export type Id = Entity.Id | Snapshot.Id
  export namespace Id {
    export const isId: TypeChecker<Id> = TypeChecker.Factory.union(
      Entity.Id.isId,
      Snapshot.Id.isId,
    )
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)

    /**
     * Gets the {@link Record.Id} from a {@link Record.Identifiable}
     */
    export function from(
      identifiable: Exclude<Entity.Identifiable, Snapshot.Identifiable>,
    ): Entity.Id
    export function from(
      identifiable: Exclude<Snapshot.Identifiable, Entity.Identifiable>,
    ): Snapshot.Id
    export function from(identifiable: Identifiable): Id
    export function from(
      identifiable: Identifiable | Attribute.Identifiable | Nullish,
    ): Id | undefined
    export function from(
      identifiable: Identifiable | Attribute.Identifiable | Nullish,
    ): Id | undefined {
      if (!identifiable) {
        return undefined
      }
      if (Snapshot.Identifiable.isIdentifiable(identifiable)) {
        // If this is for a snapshot, we get the snapshot ID
        return Snapshot.Id.from(identifiable)
      }
      // Otherwise, we assume it is for an entity, and thus get that ID
      return Entity.Id.from(identifiable)
    }
  }

  /**
   * Can be used to get a {@link ForRecord.Id}
   *
   * __NOTE:__ does not support ambiguous identifiables (i.e. values that are
   * both a {@link Entity.Identifiable} and a {@link Snapshot.Identifiable})
   *
   * @see {@link ForRecord.Id.from}
   */
  export type Identifiable =
    // NOTE: we filter out any overlap between entity and snapshot identifiables
    // since that would lead to ambiguity
    | Exclude<Entity.Identifiable, Snapshot.Identifiable>
    | Exclude<Snapshot.Identifiable, Entity.Identifiable>
  export namespace Identifiable {
    export const isIdentifiable: TypeChecker<Identifiable> =
      TypeChecker.Factory.exclude(
        // It can be either of these
        TypeChecker.Factory.union(
          Entity.Identifiable.isIdentifiable,
          Snapshot.Identifiable.isIdentifiable,
        ),
        // But not both
        TypeChecker.Factory.intersection(
          Entity.Identifiable.isIdentifiable,
          Snapshot.Identifiable.isIdentifiable,
        ),
      )
  }
}
